import { useEffect, useState } from "react";
import { getListColumns, type SPColumnMeta } from "../api/graph";
import { useGraphToken } from "../auth/useGraphToken";

/**
 * Fetches a SharePoint list's column definitions (Choice values + column
 * Description) so forms can reflect the live schema instead of a hardcoded
 * copy. Returns an empty map while loading or if the fetch fails — callers
 * should always pair this with a fallback default via `choicesFor`/`hintFor`.
 */
export function useListColumns(listId: string) {
  const getToken = useGraphToken();
  const [columns, setColumns] = useState<Record<string, SPColumnMeta>>({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const token = await getToken();
        const cols = await getListColumns(token, listId);
        if (!cancelled) setColumns(cols);
      } catch {
        // Swallow — callers fall back to their hardcoded defaults.
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [getToken, listId]);

  return { columns, loading };
}

/** Live Choice values for a column, or `fallback` if unset/not a Choice column/not loaded yet. */
export function choicesFor(
  columns: Record<string, SPColumnMeta>,
  internalName: string,
  fallback: string[]
): string[] {
  const live = columns[internalName]?.choices;
  return live && live.length > 0 ? live : fallback;
}

/** The SharePoint column's Description text, or `fallback` if it's blank/not loaded yet. */
export function hintFor(
  columns: Record<string, SPColumnMeta>,
  internalName: string,
  fallback: string
): string {
  const desc = columns[internalName]?.description;
  return desc && desc.length > 0 ? desc : fallback;
}

/**
 * Union of a column's live SharePoint Choice values and a caller-supplied set
 * (e.g. values already in use on existing rows) — for free-text/datalist
 * fields that suggest values rather than enforce a closed set, this ensures
 * the suggestions include every configured choice, not just ones already seen.
 */
export function mergeChoices(
  columns: Record<string, SPColumnMeta>,
  internalName: string,
  extra: string[]
): string[] {
  const set = new Set<string>(columns[internalName]?.choices ?? []);
  for (const v of extra) set.add(v);
  return Array.from(set).sort((a, b) => a.localeCompare(b));
}
